import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse, LoginFormData } from '../types/auth';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: LoginFormData) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const decodeJwt = (jwtToken: string): any | null => {
    try {
      const parts = jwtToken.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      console.log('初始化認證，token:', storedToken);
      if (storedToken) {
        try {
          const userData = await authAPI.getProfile(storedToken);
          console.log('獲取用戶資料成功:', userData);
          setUser(userData);
          setToken(storedToken);
        } catch (error) {
          console.error('獲取用戶資料失敗（改用 JWT 內容建立暫時使用者）:', error);
          const payload = decodeJwt(storedToken);
          if (payload) {
            setUser({
              id: Number(payload.sub) || 0,
              username: String(payload.username || ''),
              name: String(payload.name || payload.username || ''),  // 優先使用 name，fallback 到 username
              email: '',
              permission: String(payload.perm || ''),
              is_active: true,
            } as User);
          }
          setToken(storedToken);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginFormData) => {
    try {
      console.log('開始登入，credentials:', credentials);
      const response: AuthResponse = await authAPI.login(credentials);
      console.log('登入回應:', response);
      setUser(response.user);
      setToken(response.access);
      localStorage.setItem('token', response.access);
      localStorage.setItem('refresh', response.refresh);
      console.log('Token 已儲存:', response.access);
    } catch (error) {
      console.error('登入失敗:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    // 以 token 為準，避免刷新時 profile 延遲造成誤判
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
